import React, { useEffect, useMemo, useRef, useState } from "react";

// The guided chooser on /start/chooser. Every method is a badge; answering a question greys out the
// methods that don't fit it. A method fits a question unless its `facets` list the question and leave
// the answer out (a facet it doesn't list fits every answer). Answers live in the URL query.

const QUESTIONS = [
	{ key: "iv_kind", text: "What kind are your independent variables?", hint: "The predictors or grouping variables: what you think explains or sorts the scores.",
		answers: [["groups", "Groups or conditions", "Treatment vs control, three teaching methods"], ["continuous", "Continuous measures", "Hours of sleep, anxiety scores"],
			["mix", "A mix", "A treatment group plus a baseline score"], ["none", "None", "One group against a known value, or a measure on its own"]] },
	{ key: "same_people", text: "Were the same people measured more than once?",
		answers: [["different", "No, different people", "Each person is measured once"], ["same", "Yes, the same people", "Before and after, or every person in every condition"],
			["clustered", "Grouped in clusters", "Students in classes, patients in clinics"]] },
	{ key: "groups", text: "How many groups or conditions?", showIf: (a) => a.iv_kind === "groups" || a.iv_kind === "mix",
		answers: [["two", "Two"], ["three_plus", "Three or more"]] },
	{ key: "goal", text: "What are you trying to find out?", answers: [] }, // filled from props
	{ key: "question", text: "Which of these is closest to your question?", hint: "The specific kinds of question under the goal you picked.",
		showIf: (a) => Boolean(a.goal) && a.goal !== UNSURE, answers: [] }, // filled from the chosen goal; only after a real goal
	{ key: "outcome", text: "What type of data is your outcome (dependent variable)?", hint: "A single Likert item is ordinal; a scale score summed from many items counts as continuous.",
		answers: [["continuous", "Continuous", "A score or measurement you could average"], ["ordinal", "Ordinal", "Ranked categories, like a Likert item"],
			["binary", "Two categories", "Yes or no, passed or failed"], ["nominal", "Three or more categories", "No order, like diagnosis type"],
			["count", "Counts", "How many times something happened"]] },
	{ key: "outcomes", text: "How many outcomes are you analyzing together?", answers: [["one", "One"], ["several", "Two or more at once"]] },
	{ key: "iv_count", text: "How many independent variables do you have?", showIf: (a) => a.iv_kind !== "none", answers: [["one", "One"], ["several", "Two or more"]] },
	{ key: "covariate", text: "Is there a variable you want to control for?", answers: [["no", "No"], ["yes", "Yes, a covariate", "Such as a baseline score"]] },
];
const KEYS = QUESTIONS.map((q) => q.key);
const UNSURE = "unsure"; // an answer that moves you on without ruling anything out
const LIST_MAX = 8; // list methods with their descriptions once this few are left
const CLOSEST_MAX = 6; // near misses to name when nothing fits

// "One-Sample t-Test" → an italic t, as everywhere else on the site.
function MethodName({ name }) {
	const parts = name.split(/(?<![A-Za-z])([tzF])(?=-)/);
	return parts.map((p, i) => (i % 2 ? <em key={i}>{p}</em> : p));
}

function readAnswers() {
	const params = new URLSearchParams(window.location.search);
	return Object.fromEntries(KEYS.filter((k) => params.get(k)).map((k) => [k, params.get(k)]));
}

export default function MethodChooser({ families, goals }) {
	const [answers, setAnswers] = useState(readAnswers);
	const stepRefs = useRef({});

	// Questions with their answers resolved; a hidden question's answer is ignored.
	const questions = useMemo(() => QUESTIONS.map((q) => {
		if (q.key === "goal") return { ...q, answers: goals.map((g) => [g.name, g.name]) };
		if (q.key === "question") return { ...q, answers: (goals.find((g) => g.name === answers.goal)?.questions || []).map((x) => [x.slug, x.name]) };
		return q;
	}).filter((q) => !q.showIf || q.showIf(answers)), [goals, answers]);

	// Answered questions, "Not sure" included (it moves you on); only real answers filter.
	const given = useMemo(() => Object.fromEntries(questions.filter((q) => answers[q.key]).map((q) => [q.key, answers[q.key]])), [questions, answers]);
	const active = useMemo(() => Object.fromEntries(Object.entries(given).filter(([, v]) => v !== UNSURE)), [given]);
	const current = questions.find((q) => !given[q.key])?.key; // the step to answer next

	useEffect(() => {
		const query = new URLSearchParams(given).toString();
		window.history.replaceState(null, "", query ? `?${query}` : window.location.pathname);
	}, [given]);

	// Bring the next step into view when it's below the fold, so the page walks you through.
	const lastCurrent = useRef(current);
	useEffect(() => {
		if (current && lastCurrent.current !== current) {
			const el = stepRefs.current[current];
			if (el && el.getBoundingClientRect().top > window.innerHeight - 120) el.scrollIntoView({ behavior: "smooth", block: "center" });
		}
		lastCurrent.current = current;
	}, [current]);

	const label = (key, value) => questions.find((q) => q.key === key)?.answers.find(([v]) => v === value)?.[1] || value;

	// Why a method is ruled out: the answers it doesn't fit.
	const misses = (m) => Object.entries(active).filter(([key, value]) => {
		if (key === "goal") return !m.goals.includes(value);
		if (key === "question") return !m.questions.includes(value);
		return m.facets[key] && !m.facets[key].includes(value);
	}).map(([key, value]) => `“${label(key, value)}”`);

	const all = families.flatMap((f) => f.methods);
	const lit = all.filter((m) => misses(m).length === 0);
	// Near misses, most specific first: a method that answers more questions says more about your design.
	const closest = lit.length ? [] : all.filter((m) => misses(m).length === 1).sort((x, y) => Object.keys(y.facets).length - Object.keys(x.facets).length);
	const answeredCount = Object.keys(given).length;
	const filtering = Object.keys(active).length;

	const choose = (key, value) => setAnswers((a) => {
		const next = { ...a };
		if (a[key] === value) delete next[key]; else next[key] = value;
		if (key === "goal") delete next.question; // a new goal has different questions
		return next;
	});

	const pill = (on, quiet) => `cursor-pointer rounded-full border px-4 py-2 text-sm transition ${on
		? "border-teal bg-teal-50 font-semibold text-ink"
		: `border-gray-300 bg-white hover:border-teal ${quiet ? "text-gray-500" : "text-gray-800"}`}`;

	return (
		<div className="grid gap-10 lg:grid-cols-[minmax(0,7fr)_minmax(0,5fr)]">
			<div>
				<div className="flex items-center gap-3 text-sm text-gray-600" aria-live="polite">
					<span>{current ? `Question ${questions.findIndex((q) => q.key === current) + 1} of ${questions.length}` : `All ${questions.length} questions answered`}</span>
					<span className="h-1.5 flex-1 overflow-hidden rounded-full bg-gray-100"><span className="block h-full rounded-full bg-teal transition-all duration-300" style={{ width: `${(answeredCount / questions.length) * 100}%` }} /></span>
				</div>

				<ol className="mt-5 space-y-4">
					{questions.map((q, i) => {
						const done = Boolean(given[q.key]);
						const now = q.key === current;
						const later = !done && !now;
						return (
							<li key={q.key} ref={(el) => { stepRefs.current[q.key] = el; }} className="flex gap-4">
								<span aria-hidden="true" className={`mt-5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold transition ${done
									? "bg-teal text-white" : now ? "border-2 border-teal bg-white text-teal" : "border border-gray-300 bg-white text-gray-400"}`}>{done ? "✓" : i + 1}</span>
								<fieldset className={`min-w-0 flex-1 rounded-xl border bg-white p-5 transition sm:p-6 ${now
									? "border-teal shadow-[0_8px_24px_-12px_rgba(13,66,82,0.35)]" : "border-gray-200"} ${later ? "opacity-50 hover:opacity-100 focus-within:opacity-100" : ""}`}>
									<legend className="sr-only">{`Question ${i + 1}: ${q.text}`}</legend>
									<p aria-hidden="true" className="text-lg font-bold tracking-tight text-ink">{q.text}</p>
									{q.hint && <p className="mt-1 text-sm leading-snug text-gray-500">{q.hint}</p>}
									<div className="mt-4 flex flex-wrap gap-2">
										{q.answers.map(([value, text, example]) => (
											<button key={value} type="button" className={pill(given[q.key] === value)} aria-pressed={given[q.key] === value}
												title={example} onClick={() => choose(q.key, value)}>{text}</button>
										))}
										<button type="button" className={pill(given[q.key] === UNSURE, true)} aria-pressed={given[q.key] === UNSURE}
											onClick={() => choose(q.key, UNSURE)}>Not sure</button>
									</div>
								</fieldset>
							</li>
						);
					})}
				</ol>
			</div>

			<aside className="lg:sticky lg:top-6 lg:max-h-[calc(100vh-3rem)] lg:self-start lg:overflow-y-auto lg:pr-1" aria-label="Methods that fit">
				<div className="flex flex-wrap items-baseline justify-between gap-3" aria-live="polite">
					{/* The results box carries the count once there are answers and matches; this line covers the rest. */}
					{!(filtering > 0 && lit.length > 0) ? (
						<p className="text-lg text-gray-800"><span className="text-2xl font-bold text-ink">{lit.length}</span> of {all.length} methods fit {filtering ? "your answers" : "so far"}</p>
					) : <span />}
					{answeredCount > 0 && <button type="button" className="link cursor-pointer text-sm" onClick={() => setAnswers({})}>Start Over</button>}
				</div>

				{filtering > 0 && lit.length > 0 && (
					<div className="mt-4 overflow-hidden rounded-[3px] border border-teal bg-white">
						<p className="bg-teal px-5 py-3 font-semibold text-white">{lit.length === 1 ? "1 Method Fits Your Answers" : `${lit.length} Methods Fit Your Answers`}</p>
						<div className="px-5 py-4">
							{lit.length <= LIST_MAX ? (
								<>
									<ul className="space-y-2.5 text-sm leading-snug text-gray-700">
										{lit.map((m) => (
											<li key={m.slug}><a href={m.url} className="link font-semibold"><MethodName name={m.name} /></a>: {m.summary}</li>
										))}
									</ul>
									<p className="mt-4 text-sm text-gray-600">Before you commit, check the method's “This Is Not Your Test If…” section.</p>
								</>
							) : (
								<p className="text-sm leading-snug text-gray-700">That's still a lot. Answer a few more questions to narrow it down; the methods that fit are the bright badges below.</p>
							)}
						</div>
					</div>
				)}

				{lit.length === 0 && (
					<div className="mt-4 rounded-xl border border-dashed border-gray-300 bg-gray-50 p-5">
						<p className="eyebrow text-[0.7rem] text-gray-600">No Match</p>
						<p className="mt-1 text-base font-semibold text-ink">No method on the site fits every answer.</p>
						<p className="mt-1 text-sm leading-snug text-gray-700">Your design may need something the site doesn't cover yet, or one answer may be worth a second look. For an analysis that matters, check with an advisor or a statistician.</p>
						{closest.length > 0 && <p className="mt-3 text-sm font-semibold text-gray-800">Closest, each ruled out by one answer:</p>}
						{closest.length > 0 && (
							<ul className="mt-1.5 space-y-1.5 text-sm">
								{closest.slice(0, CLOSEST_MAX).map((m) => (
									<li key={m.slug}><a href={m.url} className="link font-semibold"><MethodName name={m.name} /></a> <span className="text-gray-600">doesn't fit {misses(m)[0]}</span></li>
								))}
								{closest.length > CLOSEST_MAX && <li className="text-gray-600">and {closest.length - CLOSEST_MAX} more, among the badges below</li>}
							</ul>
						)}
					</div>
				)}

				<div className="mt-6 space-y-5">
					{families.map((f) => (
						<div key={f.name}>
							<p className="eyebrow text-[0.7rem] text-spruce-700"><MethodName name={f.name} /></p>
							<div className="mt-2 flex flex-wrap gap-1.5">
								{f.methods.map((m) => {
									const out = misses(m);
									return (
										<a key={m.slug} href={m.url} title={out.length ? `Doesn't fit ${out.join(", ")}` : m.summary}
											className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[0.8rem] font-semibold transition ${out.length
												? "border-gray-200 bg-white text-gray-400 hover:text-gray-600"
												: "border-teal/25 bg-teal-50 text-teal hover:border-teal hover:bg-teal hover:text-white"}`}>
											<span><MethodName name={m.name} /></span>
										</a>
									);
								})}
							</div>
						</div>
					))}
				</div>
			</aside>
		</div>
	);
}
